import React, { useState, useMemo } from "react";
import ProtectedRoute from "../components/ProtectedRoute";
import { useTelegramLogs, TelegramLogsApiParams, exportTelegramLogs } from "../api/telegramLogs";
import { UserAutocomplete } from "../components/UserAutocomplete";

const TelegramLogsPage: React.FC = () => {
    const [userId, setUserId] = useState<string>("");
    const [chatId, setChatId] = useState<string>("");
    const [status, setStatus] = useState<string>("");
    const [errorCode, setErrorCode] = useState<string>("");
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(20);
    const [sortBy, setSortBy] = useState<"id" | "created_at" | "status" | "error_code">("created_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [isExporting, setIsExporting] = useState(false);

    const params: TelegramLogsApiParams = useMemo(() => {
        const parsedUserId = parseInt(userId, 10);
        const parsedChatId = parseInt(chatId, 10);
        const parsedStatus = parseInt(status, 10);
        const parsedErrorCode = parseInt(errorCode, 10);

        return {
            user_id: !Number.isNaN(parsedUserId) && parsedUserId > 0 ? parsedUserId : undefined,
            chat_id: !Number.isNaN(parsedChatId) && parsedChatId > 0 ? parsedChatId : undefined,
            status: !Number.isNaN(parsedStatus) ? parsedStatus : undefined,
            error_code: !Number.isNaN(parsedErrorCode) ? parsedErrorCode : undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            page,
            per_page: perPage,
            sort_by: sortBy,
            sort_order: sortOrder,
        };
    }, [userId, chatId, status, errorCode, dateFrom, dateTo, page, perPage, sortBy, sortOrder]);

    const { data, isLoading, error } = useTelegramLogs(params);

    const handleSort = (field: "id" | "created_at" | "status" | "error_code") => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
        setPage(1);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const result = await exportTelegramLogs(params);

            if (result.success && result.data && result.filename) {
                const binaryString = atob(result.data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], {
                    type: "application/vnd.ms-excel",
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = downloadUrl;
                a.download = result.filename;
                a.click();
                window.URL.revokeObjectURL(downloadUrl);
            } else {
                alert("Ошибка при экспорте: " + (result.message || "Неизвестная ошибка"));
            }
        } catch (err: any) {
            console.error("Export error:", err);
            alert("Ошибка при экспорте: " + (err.message || "Неизвестная ошибка"));
        } finally {
            setIsExporting(false);
        }
    };

    const getStatusBadgeColor = (statusCode: number) => {
        if (statusCode >= 200 && statusCode < 300) return "bg-green-500";
        if (statusCode >= 400 && statusCode < 500) return "bg-yellow-500";
        if (statusCode >= 500) return "bg-red-500";
        return "bg-gray-500";
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleString("ru-RU");
    };

    const logs = data?.data || [];
    const meta = data?.meta;

    return (
        <ProtectedRoute requiredRoles={["admin"]}>
            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Телеграм логи
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Логирование отправки сообщений в Telegram за последние 3 месяца
                    </p>
                </div>

                {/* Фильтры */}
                <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                User ID
                            </label>
                            <UserAutocomplete
                                value={userId}
                                onChange={(value) => {
                                    setUserId(value);
                                    setPage(1);
                                }}
                                placeholder="ID, email, phone, name..."
                                className="text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Chat ID
                            </label>
                            <input
                                type="number"
                                value={chatId}
                                onChange={(e) => {
                                    setChatId(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                placeholder="Telegram Chat ID"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Status
                            </label>
                            <input
                                type="number"
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                placeholder="HTTP статус"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Error Code
                            </label>
                            <input
                                type="number"
                                value={errorCode}
                                onChange={(e) => {
                                    setErrorCode(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                placeholder="Код ошибки"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Дата от
                            </label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => {
                                    setDateFrom(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Дата до
                            </label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => {
                                    setDateTo(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={() => {
                                setUserId("");
                                setChatId("");
                                setStatus("");
                                setErrorCode("");
                                setDateFrom("");
                                setDateTo("");
                                setPage(1);
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                        >
                            Сбросить фильтры
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={isExporting}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                            {isExporting ? "Экспорт..." : "Загрузить в Excel"}
                        </button>
                    </div>
                </div>

                {/* Таблица */}
                {error && (
                    <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg">
                        Ошибка загрузки данных: {error instanceof Error ? error.message : "Неизвестная ошибка"}
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th
                                        onClick={() => handleSort("id")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        ID {sortBy === "id" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Chat ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Bot
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Сообщение
                                    </th>
                                    <th
                                        onClick={() => handleSort("status")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Status {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th
                                        onClick={() => handleSort("error_code")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Error Code {sortBy === "error_code" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Описание
                                    </th>
                                    <th
                                        onClick={() => handleSort("created_at")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Дата {sortBy === "created_at" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={9} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                            Загрузка...
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                            Нет данных
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.user ? (
                                                    <div>
                                                        <div className="font-medium">
                                                            {log.user.name} {log.user.surname}
                                                        </div>
                                                        <div className="text-gray-500 dark:text-gray-400 text-xs">
                                                            ID: {log.user.id} | {log.user.email}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.chat_id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.bot_key ?? "—"}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs whitespace-normal">
                                                {log.message}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${getStatusBadgeColor(
                                                        log.status
                                                    )}`}
                                                >
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.error_code ?? "—"}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs whitespace-normal">
                                                {log.description ?? "—"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {formatDate(log.created_at)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Пагинация */}
                    {meta && meta.last_page > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                    Показано{" "}
                                    {meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0}{" "}
                                    -{" "}
                                    {Math.min(meta.current_page * meta.per_page, meta.total)}{" "}
                                    из {meta.total}
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <label className="text-sm text-gray-700 dark:text-gray-300">
                                            На странице:
                                        </label>
                                        <select
                                            value={meta.per_page}
                                            onChange={(e) => {
                                                setPerPage(Number(e.target.value));
                                                setPage(1);
                                            }}
                                            className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                            disabled={page === 1}
                                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Назад
                                        </button>
                                        <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                            Страница {meta.current_page} из {meta.last_page}
                                        </span>
                                        <button
                                            onClick={() => setPage(Math.min(meta.last_page, page + 1))}
                                            disabled={page === meta.last_page}
                                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Вперед
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ProtectedRoute>
    );
};

export default TelegramLogsPage;
